// Shared helpers for the customer gift-card shop.

// Resolve which designs a voucher offers: an empty `designIds` means "every
// available design"; a populated list is the salon's curated subset. Mirrors
// the resolution used in VoucherDetailPanel.
export const resolveDesignsForVoucher = (voucher, designs) => {
  if (!voucher) return [];
  const ids = voucher.designIds || [];
  const list = ids.length
    ? ids.map(id => designs.find(d => d.id === id)).filter(Boolean)
    : designs.filter(d => d.available);
  const resolved = list.length ? list : designs.slice(0, 1);
  // Lead with the salon's own custom (bespoke) designs.
  return [...resolved].sort((a, b) => (b.isCustom ? 1 : 0) - (a.isCustom ? 1 : 0));
};

// First denomination (or the voucher's primary value) as the default amount.
export const defaultAmountFor = (voucher) => {
  if (!voucher) return 50;
  if (voucher.denominations && voucher.denominations.length) return voucher.denominations[0];
  return voucher.value || 50;
};

// What the buyer actually pays for a chosen face value, honouring promo
// pricing. Mirrors the backend _resolveGiftCardAmounts EXACTLY so the price
// shown here equals the Stripe charge:
//   fixed   → flat amount off every denomination (face − promoValue)
//   percent → same proportion off every denomination
// Legacy catalogs that only carry the derived promo/value pair fall back to
// the ratio (percent-only, so still correct).
export const payFor = (voucher, amount) => {
  if (!voucher) return amount;
  const promoValue = Number(voucher.promoValue);
  let paid = amount;
  if (voucher.promoMode === 'fixed' && promoValue > 0) {
    paid = Math.max(0, amount - promoValue);
  } else if (voucher.promoMode === 'percent' && promoValue > 0) {
    paid = amount * (1 - promoValue / 100);
  } else if (voucher.promo != null && voucher.value && voucher.promo < voucher.value) {
    paid = amount * (voucher.promo / voucher.value);
  }
  return Math.round(paid * 100) / 100;
};

// Build the preview-voucher object VoucherCard expects from a design + amount.
// Mirrors buildPreviewVoucher in DesignPreviewPanel / VoucherDetailPanel.
export const buildPreviewVoucher = (design, value) => {
  if (!design) {
    return { name: 'Gift Card', template: 'signature', theme: 'charcoal', value, promo: null };
  }
  if (design.isCustom) {
    return {
      name: 'Gift Card',
      template: 'signature',
      theme: design.theme,
      value,
      promo: null,
      customDesign: design.config,
    };
  }
  return {
    name: 'Gift Card',
    template: design.occasion || 'signature',
    theme: design.theme,
    value,
    promo: null,
  };
};
